import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useState,
} from 'react';
import {
  create,
  type StateCreator,
  type StoreApi,
  type UseBoundStore,
  useStore,
} from 'zustand';
import { createStore } from 'zustand/vanilla';

import type { SUB_DOMAIN } from '@/constants/domain';
import type { MessageResponse } from '@/modules/wello-app';
import { WEBVIEW_TYPE } from '@/utils/webviewType';

export type OsType = 'android' | 'ios' | 'mac' | 'windows' | null;
type ViewportType = 'desktop' | 'tablet' | 'mobile';

export interface DeviceTokenData {
  apnsToken: string;
  fcm_token: string;
  os: string;
}

export enum STATUS_COLOR {
  DEFAULT = '#FFFFFF',
  LUCKY_BALL_MAIN = '#DDF2FF',
  LUCKY_BALL_OPENED = '#FFFBE5',
  CHATBOT = '#F6F6F6',
}

export { WEBVIEW_TYPE };

type AppMessageLog = Map<string, MessageResponse>;

export interface AppModalOptions {
  isOpened: boolean;
  type: 'permission' | 'exit' | 'restart' | 'forceUpdate';
}

export interface DeviceInfoStore {
  isMounted: boolean;
  setIsMounted: (isMounted: boolean) => void;

  webviewType?: WEBVIEW_TYPE;
  setWebviewType: (webviewType?: WEBVIEW_TYPE) => void;

  /** Firebase Cloud Messaging */
  fcmToken: string | null;
  setFcmToken: (firebaseToken: string | null) => void;

  tempDeviceTokenData?: DeviceTokenData;
  setTempDeviceTokenData: (deviceTokenData: DeviceTokenData) => void;

  deviceTokenData?: DeviceTokenData;
  setDeviceTokenData: (deviceTokenData: DeviceTokenData) => void;

  osType?: OsType;
  setOsType: (osType: OsType) => void;

  appMessageLog: Map<string, MessageResponse>;
  setAppMessageLog: (
    setAppMessageLogAction: (appMessageLog: AppMessageLog) => AppMessageLog,
  ) => void;

  viewportType?: ViewportType;
  setViewportType: (viewportType: ViewportType) => void;

  subDomain?: SUB_DOMAIN | null;

  appVersion?: string;
  setAppVersion: (appVersion: string) => void;

  appModal: AppModalOptions;
  setAppModal: (appModalType: AppModalOptions) => void;

  appStatusColor: STATUS_COLOR | string;
  setAppStatusColor: (statusColor: STATUS_COLOR | string) => void;
}

const createDeviceInfoState =
  (initialSubDomain?: SUB_DOMAIN | null): StateCreator<DeviceInfoStore> =>
  (set) => ({
    isMounted: false,
    setIsMounted: (isMounted) => set({ isMounted }),

    setWebviewType: (webviewType) => set({ webviewType }),

    fcmToken: null,
    setFcmToken: (fcmToken) => set({ fcmToken }),

    setTempDeviceTokenData: (deviceTokenData) =>
      set({ tempDeviceTokenData: deviceTokenData }),
    setDeviceTokenData: (deviceTokenData) => set({ deviceTokenData }),

    appMessageLog: new Map(),
    setAppMessageLog: (setAppMessageLogAction) =>
      set(({ appMessageLog }) => ({
        appMessageLog: new Map(setAppMessageLogAction(appMessageLog)),
      })),

    setOsType: (osType) => set({ osType }),

    setViewportType: (viewportType) => set({ viewportType }),

    subDomain: initialSubDomain,

    setAppVersion: (appVersion) => set({ appVersion }),

    appModal: {
      isOpened: false,
      type: 'permission',
    },
    //* forceUpdate는 터미널 상태다 — 열린 뒤 다른 타입으로 교체되면 그 모달을 닫는 순간
    //* 게이트가 영구 해제된다(버전 effect는 재실행되지 않는다). 네이티브의 openAppModal도
    //* 이 setter를 거치므로 잠금은 여기 한 곳에만 둔다.
    setAppModal: (appModal) =>
      set((state) =>
        state.appModal.type === 'forceUpdate' && state.appModal.isOpened
          ? state
          : { appModal },
      ),

    appStatusColor: STATUS_COLOR.DEFAULT,
    setAppStatusColor: (appStatusColor) => set({ appStatusColor }),
  });

const createDeviceInfoStore = (initialSubDomain?: SUB_DOMAIN | null) =>
  createStore<DeviceInfoStore>(createDeviceInfoState(initialSubDomain));

const fallbackDeviceInfoStore = create<DeviceInfoStore>(
  createDeviceInfoState(),
);

const DeviceInfoStoreContext = createContext<StoreApi<DeviceInfoStore> | null>(
  null,
);

export const DeviceInfoStoreProvider = ({
  children,
  initialSubDomain,
}: {
  children: ReactNode;
  initialSubDomain?: SUB_DOMAIN | null;
}) => {
  const [store] = useState(() => createDeviceInfoStore(initialSubDomain));

  return createElement(
    DeviceInfoStoreContext.Provider,
    { value: store },
    children,
  );
};

const useScopedDeviceInfoStore = <Selected>(
  selector: (state: DeviceInfoStore) => Selected,
) => {
  const store = useContext(DeviceInfoStoreContext);

  return useStore(store ?? fallbackDeviceInfoStore, selector);
};

export const useDeviceInfoStore = Object.assign(useScopedDeviceInfoStore, {
  getState: fallbackDeviceInfoStore.getState,
  getInitialState: fallbackDeviceInfoStore.getInitialState,
  setState: fallbackDeviceInfoStore.setState,
  subscribe: fallbackDeviceInfoStore.subscribe,
}) as UseBoundStore<StoreApi<DeviceInfoStore>>;

export const useDeviceInfoStoreApi = () =>
  useContext(DeviceInfoStoreContext) ?? fallbackDeviceInfoStore;
